import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Text, View, Pressable, useWindowDimensions, Platform, Alert, ScrollView } from 'react-native';
import { AntDesign, Ionicons, FontAwesome, MaterialIcons } from '@expo/vector-icons';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import DefaultScreen from '../../../components/Global/DefaultScreenView';
import HorizontalCarousel from '../../../components/Global/Carousel/HorizontalCarousel.native';
import handleCamera from '@utils/handleCamera';
import handleImage from '@utils/handleImage';
import apis from '@api/api';
import AnimatedLottieView from '@utils/lottieAnimation';
import { useQuery } from '@tanstack/react-query';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { Feather } from '@expo/vector-icons';
import InventoryManagement from './Components/InventoryManagement';
import { Spinner, H2, H5, H4, H3, Separator, SizableText, XStack, YStack, Card } from 'tamagui';
import PhotoGallery from './Components/PhotoGallery';
import DataSetChart from '@components/dataTables/Charts/DataSetChart';
import NotesHistory from './Components/NotesHistory';
import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Image } from 'expo-image';
import ImageEnhancementModal from './Components/ImageEnhancementModal';

// Quick Action Card Component (matching TimeLineScreen style)
const QuickActionCard = ({ icon, label, onPress, disabled = false, theme }: {
  icon: any,
  label: any,
  onPress: any,
  disabled?: boolean,
  theme: any
}) => (
  <Pressable
    onPress={onPress}
    disabled={disabled}
    style={{
      backgroundColor: theme ? '#fff' : '#374151',
      borderRadius: 12,
      padding: Platform.select({ default: 12, web: 16 }),
      alignItems: 'center',
      minWidth: Platform.select({ default: 60, web: 80 }),
      borderWidth: 1,
      borderColor: disabled ? '#D1D5DB' : 'rgb(90,192,234)',
      opacity: disabled ? 0.5 : 1,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3
    }}
  >
    {icon}
    <Text style={{
      color: theme ? '#1F2937' : '#F9FAFB',
      fontSize: Platform.select({ default: 10, web: 12 }),
      fontWeight: '500',
      marginTop: 4,
      textAlign: 'center'
    }}>
      {label}
    </Text>
  </Pressable>
);

// Status Badge Component
const StatusBadge = ({ status, theme }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return '#10B981';
      case 'inactive': return '#EF4444';
      case 'pending': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  return (
    <View style={{
      backgroundColor: getStatusColor(status),
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
      alignSelf: 'flex-start'
    }}>
      <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
        {status || 'Unknown'}
      </Text>
    </View>
  );
};

// New Components for Product Management
const SupplierManagement = ({ productId, dataContent }) => {
  const theme = themeSelector();
  const [showAddForm, setShowAddForm] = useState(false);

  // Fetch suppliers using React Query
  const { data: suppliersData, isLoading, error, refetch } = useQuery({
    queryKey: ['productSuppliers', productId],
    queryFn: async () => {
      if (!productId) return { suppliers: [] };

      try {
        const response = await apis.getProductSuppliers(productId);
        if (response && response.success) {
          return { suppliers: response.suppliers || [] };
        }
        // Fallback to dataContent if API fails
        const supplierData = dataContent?.suppliers || dataContent?.data?.suppliers || [];
        return { suppliers: Array.isArray(supplierData) ? supplierData : [] };
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        // Fallback to dataContent
        const supplierData = dataContent?.suppliers || dataContent?.data?.suppliers || [];
        return { suppliers: Array.isArray(supplierData) ? supplierData : [] };
      }
    },
    enabled: !!productId,
    refetchOnMount: true,
  });

  const suppliers = suppliersData?.suppliers || [];

  const supplierSchema = {
    name: { type: 'string', validation: 'required' },
    contact: { type: 'string', validation: 'required' },
    email: { type: 'email' },
    phone: { type: 'string' },
    address: { type: 'string' },
    leadTime: { type: 'number', validation: 'required' },
    minimumOrder: { type: 'number' },
    pricePerUnit: { type: 'number', validation: 'required' },
    currency: { type: 'dropDown', content: ['USD', 'EUR', 'GBP', 'CAD'] },
    status: { type: 'dropDown', content: ['Active', 'Inactive', 'Pending'] },
    notes: { type: 'string' }
  };

  const handleAddSupplier = async (values) => {
    try {
      const response = await apis.addProductSupplier(productId, values);
      if (response && response.success) {
        Alert.alert('Success', 'Supplier added successfully');
        setShowAddForm(false);
        refetch(); // Refresh suppliers list
      } else {
        Alert.alert('Error', 'Failed to add supplier');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to add supplier');
      console.error('Error adding supplier:', error);
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    try {
      Alert.alert(
        'Delete Supplier',
        'Are you sure you want to delete this supplier?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                const response = await apis.deleteProductSupplier(productId, supplierId);
                if (response && response.success) {
                  Alert.alert('Success', 'Supplier deleted successfully');
                  refetch(); // Refresh suppliers list
                } else {
                  Alert.alert('Error', 'Failed to delete supplier');
                }
              } catch (error) {
                Alert.alert('Error', 'Failed to delete supplier');
                console.error('Error deleting supplier:', error);
              }
            }
          }
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to delete supplier');
      console.error('Error deleting supplier:', error);
    }
  };

  return (
    <YStack gap="$4" padding="$4">
      <XStack justifyContent="space-between" alignItems="center">
        <H4>Supplier Management</H4>
        <Pressable
          onPress={() => setShowAddForm(!showAddForm)}
          style={[
            styles.modernActionButton,
            getModernActionStyle(theme, 'blue'),
            { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, height: 44 }
          ]}
        >
          <AntDesign name="plus" size={16} color={theme ? "black" : "white"} />
          <Text style={[styles.modernFieldValue, { color: theme ? "black" : "white", fontWeight: '600' }]}>Add Supplier</Text>
        </Pressable>
      </XStack>

      {showAddForm && (
        <Card padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
          <GenerateSchemaForm
            schema={supplierSchema}
            submitFunction={handleAddSupplier}
            buttonLabel="Add Supplier"
            buttonColor="rgb(90,192,234)"
            scrollEnabled={true}
            customStyling={{ minHeight: 400 }}
          />
        </Card>
      )}

      {isLoading ? (
        <View style={[styles.modernInfoRow, { alignItems: 'center', padding: 20 }]}>
          <Spinner size="large" color="rgb(90,192,234)" />
        </View>
      ) : (
        <YStack gap="$3">
          {suppliers.length === 0 ? (
              <Card padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
              <Text style={[styles.modernFieldValue, { textAlign: 'center', color: getModernTextColor(theme, 'muted') }]}>
                No suppliers added yet. Add your first supplier above.
              </Text>
            </Card>
          ) : (
            suppliers.map((supplier, index) => (
              <Card key={supplier.id || index} padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
                <XStack justifyContent="space-between" alignItems="flex-start">
                  <YStack flex={1} gap="$2">
                    <XStack alignItems="center" gap="$2">
                      <Text style={styles.modernCardTitle}>{supplier.name}</Text>
                      <View style={[
                        styles.modernActionButton,
                        supplier.status === 'Active' ? getModernActionStyle(theme, 'green') : 
                        supplier.status === 'Pending' ? getModernActionStyle(theme, 'amber') : 
                        getModernActionStyle(theme, 'red'),
                        { paddingHorizontal: 8, paddingVertical: 4, height: 'auto' }
                      ]}>
                        <Text style={[styles.modernFieldLabel, { color: theme ? "black" : "white", fontSize: 10, fontWeight: '600' }]}>
                          {supplier.status || 'Unknown'}
                        </Text>
                      </View>
                    </XStack>
                    
                    <XStack gap="$4" flexWrap="wrap">
                      <YStack>
                        <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Contact</Text>
                        <Text style={styles.modernFieldValue}>{supplier.contact}</Text>
                      </YStack>
                      <YStack>
                        <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Price/Unit</Text>
                        <Text style={styles.modernFieldValue}>{supplier.currency || '$'}{supplier.pricePerUnit}</Text>
                      </YStack>
                      <YStack>
                        <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Lead Time</Text>
                        <Text style={styles.modernFieldValue}>{supplier.leadTime} days</Text>
                      </YStack>
                    </XStack>

                    {supplier.email && (
                      <Text style={[styles.modernFieldValue, { fontSize: 14, color: getModernTextColor(theme, 'muted') }]}>📧 {supplier.email}</Text>
                    )}
                    {supplier.phone && (
                      <Text style={[styles.modernFieldValue, { fontSize: 14, color: getModernTextColor(theme, 'muted') }]}>📞 {supplier.phone}</Text>
                    )}
                  </YStack>
                  
                  <Pressable
                    onPress={() => handleDeleteSupplier(supplier.id)}
                    style={[
                      styles.modernIconButton,
                      getModernActionStyle(theme, 'red')
                    ]}
                  >
                    <AntDesign name="delete" size={16} color={theme ? "black" : "white"} />
                  </Pressable>
                </XStack>
              </Card>
            ))
          )}
        </YStack>
      )}
    </YStack>
  );
};

const PricingManagement = ({ productId }) => {
  const theme = themeSelector();
  const [currentPricing, setCurrentPricing] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  const fetchProductDetails = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const response = await apis.getProductDetails(productId);
      if (response && response.success) {
        setCurrentPricing(response.product.pricing || {});
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const pricingSchema = {
    basePrice: { type: 'number', validation: 'required' },
    currency: { type: 'dropDown', content: ['USD', 'EUR', 'GBP', 'CAD'] },
    discountPrice: { type: 'number' },
    wholesalePrice: { type: 'number' },
    minimumQuantity: { type: 'number' },
    maxQuantityDiscount: { type: 'number' },
    taxRate: { type: 'number' },
    margin: { type: 'number' },
    costPrice: { type: 'number', validation: 'required' },
    priceValidFrom: { type: 'date' },
    priceValidTo: { type: 'date' }
  };

  const handleUpdatePricing = async (values) => {
    try {
      const response = await apis.updateProductPricing(productId, values);
      if (response && response.success) {
        Alert.alert('Success', 'Product pricing updated successfully');
        setCurrentPricing(response.pricing);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to update pricing');
      console.error('Error updating pricing:', error);
    }
  };

  return (
    <YStack gap="$4" padding="$4">
      {/* Current Pricing Display */}
      {Object.keys(currentPricing).length > 0 && (
        <Card padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
          <Text style={styles.modernPanelTitle}>Current Pricing</Text>
          <XStack gap="$4" flexWrap="wrap">
            <YStack>
              <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Base Price</Text>
              <Text style={[styles.modernFieldValue, { fontSize: 18, fontWeight: '700', color: 'rgb(90,192,234)' }]}>
                {currentPricing.currency || '$'}{currentPricing.basePrice || 'N/A'}
              </Text>
            </YStack>
            <YStack>
              <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Cost Price</Text>
              <Text style={[styles.modernFieldValue, { fontSize: 16, fontWeight: '500' }]}>
                {currentPricing.currency || '$'}{currentPricing.costPrice || 'N/A'}
              </Text>
            </YStack>
            <YStack>
              <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Margin</Text>
              <Text style={[styles.modernFieldValue, { fontSize: 16, fontWeight: '500' }]}>
                {currentPricing.margin || 'N/A'}%
              </Text>
            </YStack>
          </XStack>
        </Card>
      )}

      {/* Update Pricing Form */}
      <Card padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
        <Text style={styles.modernPanelTitle}>Update Pricing</Text>
        {loading ? (
          <View style={{ alignItems: 'center', padding: 20 }}>
            <Spinner size="large" color="rgb(90,192,234)" />
          </View>
        ) : (
          <GenerateSchemaForm
            schema={pricingSchema}
            submitFunction={handleUpdatePricing}
            buttonLabel="Update Pricing"
            buttonColor="rgb(90,192,234)"
            scrollEnabled={true}
            customStyling={{ minHeight: 400 }}
          />
        )}
      </Card>
    </YStack>
  );
};

const QualityControl = ({ productId }) => {
  const theme = themeSelector();
  const [qualityChecks, setQualityChecks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  const fetchProductDetails = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const response = await apis.getProductDetails(productId);
      if (response && response.success) {
        setQualityChecks(response.product.qualityChecks || []);
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const qualitySchema = {
    inspector: { type: 'string', validation: 'required' },
    inspectionDate: { type: 'date', validation: 'required' },
    qualityRating: { type: 'dropDown', content: ['Excellent', 'Good', 'Fair', 'Poor'] },
    defectRate: { type: 'number' },
    batchNumber: { type: 'string' },
    testResults: { type: 'string' },
    certifications: { type: 'tags' },
    complianceStatus: { type: 'dropDown', content: ['Compliant', 'Non-Compliant', 'Pending'] },
    actionRequired: { type: 'string' },
    notes: { type: 'string' }
  };

  const handleQualityCheck = async (values) => {
    try {
      const response = await apis.addQualityCheck(productId, values);
      if (response && response.success) {
        Alert.alert('Success', 'Quality check recorded successfully');
        fetchProductDetails(); // Refresh list
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to record quality check');
      console.error('Error adding quality check:', error);
    }
  };

  const getQualityColor = (rating) => {
    switch (rating) {
      case 'Excellent': return '#10B981';
      case 'Good': return '#3B82F6';
      case 'Fair': return '#F59E0B';
      case 'Poor': return '#EF4444';
      default: return '#6B7280';
    }
  };

  return (
    <YStack gap="$4" padding="$4">
      {/* Quality History */}
      {qualityChecks.length > 0 && (
        <Card padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
          <Text style={styles.modernPanelTitle}>Quality Check History</Text>
          <YStack gap="$2" maxHeight={200} style={{ overflow: 'scroll' }}>
            {qualityChecks.map((check, index) => (
              <XStack key={check.id || index} padding="$2" style={[
                styles.modernInfoRow,
                { 
                  borderLeftWidth: 4,
                  borderLeftColor: getQualityColor(check.qualityRating)
                }
              ]}>
                <YStack flex={1}>
                  <XStack justifyContent="space-between" alignItems="center">
                    <Text style={[styles.modernFieldValue, { fontWeight: '600' }]}>{check.inspector}</Text>
                    <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>
                      {new Date(check.inspectionDate).toLocaleDateString()}
                    </Text>
                  </XStack>
                  <XStack gap="$3" marginTop="$1">
                    <Text style={[styles.modernFieldLabel, { fontSize: 12 }]}>Rating: {check.qualityRating}</Text>
                    {check.batchNumber && <Text style={[styles.modernFieldLabel, { fontSize: 12 }]}>Batch: {check.batchNumber}</Text>}
                    {check.defectRate && <Text style={[styles.modernFieldLabel, { fontSize: 12 }]}>Defects: {check.defectRate}%</Text>}
                  </XStack>
                </YStack>
              </XStack>
            ))}
          </YStack>
        </Card>
      )}

      {/* Add New Quality Check */}
      <Card padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
        <Text style={styles.modernPanelTitle}>Record Quality Check</Text>
        {loading ? (
          <View style={[styles.modernInfoRow, { alignItems: 'center', padding: 20 }]}>
            <Spinner size="large" color="rgb(90,192,234)" />
          </View>
        ) : (
          <GenerateSchemaForm
            schema={qualitySchema}
            submitFunction={handleQualityCheck}
            buttonLabel="Record Quality Check"
            buttonColor="rgb(90,192,234)"
            scrollEnabled={true}
            customStyling={{ minHeight: 400 }}
          />
        )}
      </Card>
    </YStack>
  );
};

const ProductAnalytics = ({ productId }) => {
  const theme = themeSelector();
  const [analytics, setAnalytics] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [productId]);

  const fetchAnalytics = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const response = await apis.getProductAnalytics(productId);
      if (response && response.success) {
        setAnalytics(response.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={{ alignItems: 'center', padding: 50 }}>
        <Spinner size="large" color="rgb(90,192,234)" />
        <Text style={{ marginTop: 10, color: '#666' }}>Loading analytics...</Text>
      </View>
    );
  }

  return (
    <YStack gap="$4" padding="$4">
      {/* Key Metrics Cards */}
      <XStack gap="$4" flexWrap="wrap">
        <Card flex={1} minWidth={150} padding="$4" backgroundColor={theme ? '$background' : '$backgroundTransparent'}>
          <YStack alignItems="center">
            <Text style={{ fontSize: 24, fontWeight: '700', color: 'rgb(90,192,234)' }}>
              {analytics.totalSales || 0}
            </Text>
            <Text style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>Total Sales</Text>
          </YStack>
        </Card>
        
        <Card flex={1} minWidth={150} padding="$4" backgroundColor={theme ? '$background' : '$backgroundTransparent'}>
          <YStack alignItems="center">
            <Text style={{ fontSize: 24, fontWeight: '700', color: '#10B981' }}>
              ${analytics.totalRevenue || 0}
            </Text>
            <Text style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>Total Revenue</Text>
          </YStack>
        </Card>

        <Card flex={1} minWidth={150} padding="$4" backgroundColor={theme ? '$background' : '$backgroundTransparent'}>
          <YStack alignItems="center">
            <Text style={{ fontSize: 24, fontWeight: '700', color: '#F59E0B' }}>
              {analytics.averageRating || 0}⭐
            </Text>
            <Text style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>Avg Rating</Text>
          </YStack>
        </Card>

        <Card flex={1} minWidth={150} padding="$4" >
          <YStack alignItems="center">
            <Text style={[styles.modernFieldValue, { fontSize: 24, fontWeight: '700', color: '#8B5CF6' }]}>
              {analytics.totalReviews || 0}
            </Text>
            <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted'), textAlign: 'center' }]}>Total Reviews</Text>
          </YStack>
        </Card>
      </XStack>

      {/* Charts Section */}
      <XStack gap="$4" flexWrap="wrap">
        <Card flex={1} minWidth={300} padding="$4" >
          <Text style={[styles.modernPanelTitle, { textAlign: 'center', marginBottom: 12 }]}>Sales Performance</Text>
          <View style={[styles.modernInfoRow, { height: 200, justifyContent: 'center', alignItems: 'center', borderRadius: 10 }]}>
            {analytics.monthlyData && analytics.monthlyData.length > 0 ? (
              <YStack alignItems="center">
                <Text style={[styles.modernFieldValue, { fontWeight: '600', marginBottom: 10 }]}>Monthly Sales Data</Text>
                {analytics.monthlyData.map((item, index) => (
                  <XStack key={index} justifyContent="space-between" width="80%" marginBottom={5}>
                    <Text style={styles.modernFieldValue}>{item.month}:</Text>
                    <Text style={[styles.modernFieldValue, { fontWeight: '600' }]}>{item.sales} units</Text>
                  </XStack>
                ))}
              </YStack>
            ) : (
              <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'muted') }]}>No sales data available</Text>
            )}
          </View>
        </Card>

        <Card flex={1} minWidth={300} padding="$4" >
          <Text style={[styles.modernPanelTitle, { textAlign: 'center', marginBottom: 12 }]}>Inventory Status</Text>
          <View style={[styles.modernInfoRow, { height: 200, justifyContent: 'center', alignItems: 'center', borderRadius: 10 }]}>
            {analytics.inventoryData ? (
              <YStack alignItems="center" gap="$2">
                <XStack gap="$4">
                  <YStack alignItems="center">
                    <Text style={[styles.modernFieldValue, { fontSize: 20, fontWeight: '700', color: 'rgb(90,192,234)' }]}>
                      {analytics.inventoryData.currentStock}
                    </Text>
                    <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Current Stock</Text>
                  </YStack>
                  <YStack alignItems="center">
                    <Text style={[styles.modernFieldValue, { fontSize: 20, fontWeight: '700', color: '#F59E0B' }]}>
                      {analytics.inventoryData.reservedStock}
                    </Text>
                    <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'muted') }]}>Reserved</Text>
                  </YStack>
                </XStack>
                
                <XStack gap="$4" marginTop="$3">
                  <YStack alignItems="center">
                    <Text style={{ fontSize: 18, fontWeight: '600', color: '#10B981' }}>
                      {analytics.inventoryData.availableStock}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#666' }}>Available</Text>
                  </YStack>
                  <YStack alignItems="center">
                    <Text style={{ fontSize: 18, fontWeight: '600', color: '#EF4444' }}>
                      {analytics.inventoryData.reorderLevel}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#666' }}>Reorder Level</Text>
                  </YStack>
                </XStack>

                {analytics.inventoryData.currentStock <= analytics.inventoryData.reorderLevel && (
                  <Text style={{ 
                    color: '#dc2626', 
                    fontWeight: '600', 
                    backgroundColor: '#fee2e2', 
                    padding: 8, 
                    borderRadius: 6,
                    marginTop: 10,
                    borderWidth: 1,
                    borderColor: '#fca5a5'
                  }}>
                    ⚠️ Reorder Required
                  </Text>
                )}
              </YStack>
            ) : (
              <Text style={{ color: '#666' }}>No inventory data available</Text>
            )}
          </View>
        </Card>
      </XStack>
    </YStack>
  );
};

const ProductScreen = ({ route }) => {
  const { dataId, contentId } = route?.params || {};
  const [editPhotos, setEditPhotos] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(0);
  const [noteHis, setNoteHis] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [enhancementModalVisible, setEnhancementModalVisible] = useState(false);
  const [selectedImageForEnhancement, setSelectedImageForEnhancement] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const theme = themeSelector();

  const { status, data: initialData, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: [`productData`, dataId, contentId],
    queryFn: async () => {
      if (!dataId || !contentId) {
        console.warn('Missing dataId or contentId:', { dataId, contentId });
        return null;
      }

      console.log('Fetching product data with:', { dataId, contentId });
      const response = await apis.getDataContentById(dataId, contentId);
      console.log('Fetched product data (raw):', JSON.stringify(response, null, 2));

      if (!response) {
        console.error('No data returned from API');
        return null;
      }

      // Extract schema and notes
      const schema = response.schema || response.data?.dataId?.dataSchema || response.dataId?.dataSchema || {};
      const notes = response.notes || response.data?.notes;
      const data = response.data || response;

      //Remove notes from data object
      const { notes: _, ...rest } = data;

      // Normalize structure - if data is flat (has product_name, price at root), wrap it in data property
      let normalizedData = rest;

      // Check if data has product fields at root level (flat structure)
      if (rest.product_name || rest.price || rest.product_description || (rest.images && !rest.data)) {
        console.log('Data is flat, normalizing structure');
        normalizedData = {
          ...rest,
          data: rest.data || rest, // Use nested data if exists, otherwise use flat
          dataImages: rest.dataImages || rest.images || [],
          images: rest.images || rest.dataImages || []
        };
      } else if (rest.data && typeof rest.data === 'object') {
        // Data is already nested
        normalizedData = {
          ...rest,
          dataImages: rest.dataImages || rest.data?.images || [],
          images: rest.images || rest.dataImages || []
        };
      }

      console.log('Normalized product data:', JSON.stringify(normalizedData, null, 2));
      setNoteHis(notes?.[0]?.comments || []);
      return {
        ...normalizedData,
        schema: schema
      };
    },
    enabled: !!dataId && !!contentId,
    refetchOnMount: true,
  });

  // Normalize data structure - ensure dataContent.data always contains product fields
  const dataContent = useMemo(() => {
    if (!initialData) return {};

    // Check if data is already properly nested
    if (initialData.data && typeof initialData.data === 'object' &&
      (initialData.data.product_name || initialData.data.name || initialData.data.price ||
        Object.keys(initialData.data).length > 0)) {
      // Already nested correctly, return as-is
      return {
        ...initialData,
        dataImages: initialData.dataImages || initialData.images || initialData.data?.images || [],
        images: initialData.images || initialData.dataImages || initialData.data?.images || []
      };
    }

    // Data is flat - wrap it properly
    return {
      ...initialData,
      data: initialData.data || initialData, // Ensure data property exists and points to product fields
      dataImages: initialData.dataImages || initialData.images || [],
      images: initialData.images || initialData.dataImages || []
    };
  }, [initialData]);

  const loading = isLoading || isFetching;

  // Debug logging
  useEffect(() => {
    console.log('ProductScreen params:', { dataId, contentId });
    console.log('ProductScreen initialData:', initialData);
    console.log('ProductScreen dataContent:', dataContent);
    console.log('ProductScreen dataContent.data:', dataContent?.data);
    console.log('ProductScreen loading:', loading);
    console.log('ProductScreen error:', error);
  }, [dataId, contentId, initialData, dataContent, loading, error]);

  // Comprehensive type detection system
  const itemTypeConfig = useMemo(() => {
    // Get data from either nested (dataContent.data) or flat (dataContent) structure
    const nestedData = dataContent?.data || {};
    const flatData = dataContent || {};
    const data = Object.keys(nestedData).length > 0 ? nestedData : flatData;

    const dataTags = Array.isArray(dataContent?.dataTags || initialData?.dataTags)
      ? (dataContent?.dataTags || initialData?.dataTags || [])
      : [];
    const category = (data.category || data.productCategory || data.serviceCategory || data.propertyCategory || '').toLowerCase();
    const type = (data.type || '').toLowerCase();

    // Normalize tags to lowercase for comparison
    const normalizedTags = dataTags.map(tag => String(tag).toLowerCase());

    // Determine item type based on tags and data
    let detectedType = 'product'; // default
    let typeLabel = 'Product';
    let icon = 'inventory-2';
    let showInventory = true;
    let showSuppliers = true;
    let showQuality = true;
    let showStockLevel = true;

    // Check for property/listing
    if (
      normalizedTags.includes('property') ||
      normalizedTags.includes('properties') ||
      normalizedTags.includes('listing') ||
      normalizedTags.includes('listings') ||
      category.includes('property') ||
      category.includes('listing') ||
      type === 'property' ||
      type === 'listing'
    ) {
      detectedType = 'property';
      typeLabel = 'Property';
      icon = 'home';
      showInventory = false;
      showSuppliers = false;
      showQuality = false;
      showStockLevel = false;
    }
    // Check for service
    else if (
      normalizedTags.includes('service') ||
      normalizedTags.includes('services') ||
      type === 'service' ||
      category.includes('service')
    ) {
      detectedType = 'service';
      typeLabel = 'Service';
      icon = 'room-service';
      showInventory = false;
      showSuppliers = false;
      showQuality = false;
      showStockLevel = false;
    }
    // Check for product (default, but explicit check)
    else if (
      normalizedTags.includes('product') ||
      normalizedTags.includes('products') ||
      type === 'product' ||
      category.includes('product')
    ) {
      detectedType = 'product';
      typeLabel = 'Product';
      icon = 'inventory-2';
      showInventory = true;
      showSuppliers = true;
      showQuality = true;
      showStockLevel = true;
    }

    return {
      type: detectedType,
      label: typeLabel,
      icon,
      showInventory,
      showSuppliers,
      showQuality,
      showStockLevel,
      isService: detectedType === 'service',
      isProperty: detectedType === 'property',
      isProduct: detectedType === 'product'
    };
  }, [dataContent?.data, dataContent?.dataTags, initialData?.dataTags]);

  const itemName = useMemo(() => {
    // Check both nested and flat structures
    const nestedData = dataContent?.data || {};
    const flatData = dataContent || {};
    const data = Object.keys(nestedData).length > 0 && !nestedData.product_name ? nestedData : flatData;

    return data.name ||
      data.title ||
      data.productName ||
      data.product_name ||
      data.serviceName ||
      data.propertyName ||
      data.listingName ||
      `${itemTypeConfig.label} Name`;
  }, [dataContent, itemTypeConfig.label]);

  const _handleUpdate = async (values) => {
    await apis.updateDataItem(contentId, values);
  };

  const handleAddCamera = useCallback(async () => {
    const res = await handleCamera();
    await apis.addDataImage(dataId, contentId, res);
    refetch();
  }, [dataId, contentId, refetch]);

  const handleAddImage = useCallback(async () => {
    const res = await handleImage(true);
    await apis.addDataImage(dataId, contentId, res);
    refetch();
  }, [dataId, contentId, refetch]);

  const picActions = [
    {
      function: handleAddCamera,
      icon: 'camera',
    },
    {
      function: handleAddImage,
      icon: 'picture',
    },
    {
      function: () => setEditPhotos(!editPhotos),
      icon: 'edit',
    },
  ];

  const productActions = [
    {
      function: () => setSelectedItem(0),
      icon: <FontAwesome5 name="truck-loading" size={24} color="black" />,
      component: <InventoryManagement dataId={dataId} />,
    },
    {
      function: () => setSelectedItem(1),
      icon: 'picture',
      component: <PhotoGallery images={dataContent?.dataImages || dataContent?.images || []} />,
    },
    {
      function: () => setSelectedItem(2),
      icon: <Ionicons name="document-text-outline" size={24} color="black" />,
      component: <Text>Documents</Text>
    },
    {
      function: () => setSelectedItem(3),
      icon: 'profile',
      component: <Text>Inventory</Text>
    },
    {
      function: () => setSelectedItem(4),
      icon: <FontAwesome name="history" size={24} color="black" />,
      component: <NotesHistory dataId={contentId} notesHistory={noteHis} />
    },
    {
      function: () => setSelectedItem(5),
      icon: <Feather name="star" size={24} color="black" />,
      component: <Text>Reviews</Text>
    }
  ];

  // Helper to get field value from either nested (data.data.field) or flat (data.field) structure
  const getField = useCallback((fieldName, defaultValue = null) => {
    // Check nested first (dataContent.data.fieldName)
    const nested = dataContent?.data?.[fieldName];
    if (nested !== undefined && nested !== null && nested !== '') {
      return nested;
    }

    // Check flat structure (dataContent.fieldName)
    const flat = dataContent?.[fieldName];
    if (flat !== undefined && flat !== null && flat !== '') {
      return flat;
    }

    return defaultValue;
  }, [dataContent]);

  // Helper to get images array from either structure
  const getImages = useCallback(() => {
    return dataContent?.dataImages || dataContent?.images || dataContent?.data?.images || dataContent?.data?.dataImages || [];
  }, [dataContent]);

  // Extract common data values - check both nested and flat structures
  const productSKU = getField('sku') || getField('productCode') || getField('code') || getField('serviceCode') || getField('propertyId') || 'N/A';
  const productCategory = getField('category') || getField('productCategory') || getField('serviceCategory') || getField('propertyCategory') || 'N/A';
  const productStatus = getField('status') || getField('productStatus') || getField('serviceStatus') || getField('propertyStatus') || 'Active';
  const productPrice = getField('price') || getField('basePrice') || getField('servicePrice') || getField('propertyPrice') || 0;
  const inventoryCount = getField('stock') || getField('inventory') || 0;
  const supplierCount = dataContent?.suppliers?.length || 0;
  const duration = getField('duration') || getField('serviceDuration') || 'N/A';
  const availability = getField('availability') || getField('serviceAvailability') || 'Available';

  return (
    <DefaultScreen
      backButton
      style={{ flex: 1 }}
      searchBar={{
        showSearch: false,
        customIcons: [
          {
            functionName: "refresh",
            icon: <Ionicons name="refresh" size={16} color="white" />,
            function: () => refetch()
          },
          {
            functionName: "edit",
            icon: <MaterialIcons name="edit" size={16} color="white" />,
            function: () => setEditOpen(true)
          },
          {
            functionName: "photos",
            icon: <MaterialIcons name="photo-camera" size={16} color="white" />,
            function: () => setEditPhotos(!editPhotos)
          }
        ]
      }}
    >
      {!dataId || !contentId ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
          <Text style={{ color: theme ? '#EF4444' : '#FCA5A5', textAlign: 'center', fontSize: 16, marginBottom: 10 }}>
            Missing Parameters
          </Text>
          <Text style={{ color: theme ? '#6B7280' : '#9CA3AF', textAlign: 'center' }}>
            dataId: {dataId ? '✓' : '✗'} | contentId: {contentId ? '✓' : '✗'}
          </Text>
        </View>
      ) : error ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
          <Text style={{ color: theme ? '#EF4444' : '#FCA5A5', textAlign: 'center', fontSize: 16, marginBottom: 10 }}>
            Error Loading Product
          </Text>
          <Text style={{ color: theme ? '#6B7280' : '#9CA3AF', textAlign: 'center' }}>
            {error?.message || 'Failed to load product details'}
          </Text>
        </View>
      ) : loading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
          <AnimatedLottieView
            source={require('@assets/animations/spin-leaf-loader.json')}
                style={{ height: 120, width: 120 }}
            autoPlay
            loop
          />
              <Text style={{ marginTop: 16, color: theme ? '#6B7280' : '#9CA3AF', textAlign: 'center' }}>
            Loading {itemTypeConfig.label.toLowerCase()} details...
          </Text>
            </View>
      ) : (
              <YStack flex={1} backgroundColor={theme ? '#F9FAFB' : '#111827'}>
                {/* Modern Product Header - matching TimeLineScreen style */}
                <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" margin="$2" borderRadius={16}>
                  <XStack alignItems="center" gap="$4">
                    {/* Product Image/Icon */}
                    <View style={{
                      width: 80,
                      height: 80,
                      borderRadius: 40,
                      backgroundColor: theme ? '#E5E7EB' : '#374151',
                      overflow: 'hidden',
                      borderWidth: 3,
                      borderColor: 'rgb(90,192,234)',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {getImages()?.[0] ? (
                        <Image
                          contentFit='cover'
                          source={{ uri: getImages()[0] }}
                          style={{ width: '100%', height: '100%' }}
                        />
                      ) : (
                        <MaterialIcons
                            name={itemTypeConfig.icon as any}
                            size={40}
                            color={theme ? '#6B7280' : '#9CA3AF'}
                          />
                      )}
                    </View>

                    {/* Product Info */}
                    <YStack flex={1} gap="$2">
                      <XStack alignItems="center" gap="$2">
                        <H4 color={theme ? '#1F2937' : '#F9FAFB'}>
                        {itemName}
                        </H4>
                        <StatusBadge status={productStatus} theme={theme} />
                        </XStack>

                      <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'}>
                        {itemTypeConfig.isProperty ? 'Property ID' : itemTypeConfig.isService ? 'Service Code' : 'SKU'}: {productSKU}
                      </SizableText>

                      <SizableText size="$3" color={theme ? '#6B7280' : '#9CA3AF'}>
                        Category: {productCategory}
                      </SizableText>

                      {productPrice > 0 && (
                        <SizableText size="$3" color={theme ? '#10B981' : '#22c55e'} fontWeight="600">
                          ${productPrice}
                        </SizableText>
                      )}
                    </YStack>
                  </XStack>
                </Card>

                {/* Navigation Tabs - horizontally scrollable */}
                <Card backgroundColor={theme ? '#ffffff' : '#374151'} margin="$2" borderRadius={16} maxHeight={70}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{
                      paddingHorizontal: Platform.select({ default: 4, web: 8 }),
                      paddingVertical: 8,
                      gap: 4
                    }}
                  >
                    <XStack gap="$1" alignItems="center">
                      {[
                        { key: 'overview', label: 'Overview', icon: 'visibility' as const, show: true },
                        { key: 'gallery', label: 'Gallery', icon: 'photo-library' as const, show: true },
                        { key: 'inventory', label: 'Inventory', icon: 'inventory' as const, show: itemTypeConfig.showInventory },
                        { key: 'suppliers', label: 'Suppliers', icon: 'local-shipping' as const, show: itemTypeConfig.showSuppliers },
                        { key: 'pricing', label: 'Pricing', icon: 'payments' as const, show: true },
                        { key: 'quality', label: 'Quality', icon: 'verified' as const, show: itemTypeConfig.showQuality },
                        { key: 'analytics', label: 'Analytics', icon: 'analytics' as const, show: true }
                      ].filter(tab => tab.show).map((tab) => (
                      <Pressable
                        key={tab.key}
                        onPress={() => setActiveTab(tab.key)}
                          style={{
                            minWidth: 80,
                            paddingVertical: 12,
                            paddingHorizontal: Platform.select({ default: 8, web: 16 }),
                            borderRadius: 12,
                            backgroundColor: activeTab === tab.key ? 'rgb(90,192,234)' : 'transparent',
                            alignItems: 'center'
                          }}
                      >
                        <MaterialIcons
                          name={tab.icon}
                            size={Platform.select({ default: 18, web: 20 })}
                            color={activeTab === tab.key ? 'white' : (theme ? '#6B7280' : '#9CA3AF')}
                          />
                          <SizableText
                            size={Platform.select({ default: "$1", web: "$2" })}
                            color={activeTab === tab.key ? 'white' : (theme ? '#6B7280' : '#9CA3AF')}
                            marginTop="$1"
                          >
                          {tab.label}
                          </SizableText>
                      </Pressable>
                    ))}
                  </XStack>
                  </ScrollView>
                </Card>

                {/* Content Area - scrollable */}
                <YStack flex={1} paddingHorizontal={Platform.select({ default: "$1", web: "$2" })}>
                  <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{ paddingBottom: 20 }}
                    showsVerticalScrollIndicator={true}
                  >
                    {activeTab === 'overview' && (
                      <YStack gap="$3">
                        {editOpen ? (
                          <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                            {!isLoading && dataContent?.data && (
                              <GenerateSchemaForm
                                retainInitialValues
                                customStyling={{ minHeight: 300 }}
                                Progress={editOpen}
                                hideButton={false}
                                nullify={['null', 'product-image', 'product-images']}
                                submitFunction={_handleUpdate}
                                initialData={dataContent?.data || {}}
                                hasTitle={false}
                                buttonLabel='Save Changes'
                                noBackground
                                schema={initialData?.schema || dataContent?.schema || {}}
                              />
                            )}
                          </Card>
                        ) : (
                            <>
                              {/* Product Description Card - Always Show */}
                              <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                                <YStack gap="$3">
                                  <XStack justifyContent="space-between" alignItems="center">
                                    <H4 color={theme ? '#1F2937' : '#F9FAFB'}>Description</H4>
                                    <Pressable
                                      onPress={() => setEditOpen(true)}
                                      style={{
                                        paddingHorizontal: 12,
                                        paddingVertical: 6,
                                        borderRadius: 8,
                                        backgroundColor: 'rgb(90,192,234)',
                                      }}
                                    >
                                      <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                                        Edit
                                      </Text>
                                    </Pressable>
                                  </XStack>
                                  <SizableText size="$3" color={theme ? '#6B7280' : '#9CA3AF'} style={{ lineHeight: 22 }}>
                                    {getField('product_description') || getField('description') || 'No description available for this product.'}
                                  </SizableText>
                                </YStack>
                              </Card>

                              {/* Product Information Grid */}
                              <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                                <YStack gap="$4">
                                  <H4 color={theme ? '#1F2937' : '#F9FAFB'}>Product Information</H4>

                                  <YStack gap="$3">
                                    <XStack justifyContent="space-between" alignItems="center" paddingVertical="$2">
                                      <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'} fontWeight="600">
                                        {itemTypeConfig.isProperty ? 'Property ID' : itemTypeConfig.isService ? 'Service Code' : 'SKU'}
                                      </SizableText>
                                      <SizableText size="$4" color={theme ? '#1F2937' : '#F9FAFB'} fontWeight="700">
                                        {productSKU}
                                      </SizableText>
                                    </XStack>

                                    <Separator borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'} />

                                    <XStack justifyContent="space-between" alignItems="center" paddingVertical="$2">
                                      <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'} fontWeight="600">
                                        Category
                                      </SizableText>
                                      <SizableText size="$4" color={theme ? '#1F2937' : '#F9FAFB'}>
                                        {productCategory}
                                      </SizableText>
                                    </XStack>

                                    <Separator borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'} />

                                    <XStack justifyContent="space-between" alignItems="center" paddingVertical="$2">
                                      <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'} fontWeight="600">
                                        Status
                                      </SizableText>
                                      <StatusBadge status={productStatus} theme={theme} />
                                    </XStack>

                                    {productPrice > 0 && (
                                      <>
                                        <Separator borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'} />
                                        <XStack justifyContent="space-between" alignItems="center" paddingVertical="$2">
                                          <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'} fontWeight="600">
                                            Price
                                          </SizableText>
                                          <SizableText size="$4" color="#10B981" fontWeight="700">
                                            ${productPrice}
                                          </SizableText>
                                        </XStack>
                                      </>
                                    )}
                                  </YStack>
                                </YStack>
                              </Card>

                              {/* Product Details - Show all available fields */}
                              {Object.keys(dataContent?.data || {}).length > 0 && (
                                <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                                  <YStack gap="$3">
                                    <H4 color={theme ? '#1F2937' : '#F9FAFB'}>Additional Details</H4>
                                    <YStack gap="$2">
                                      {Object.entries(dataContent?.data || {}).map(([key, value]) => {
                                        // Skip metadata and already displayed fields
                                        if (key === 'id' || key === '_id' || key === 'dataId' || key === '__v' ||
                                          key === 'createdAt' || key === 'updatedAt' || key === 'posted_by' ||
                                          key === 'images' || key === 'product_name' || key === 'name' ||
                                          key === 'price' || key === 'status' || key === 'category' ||
                                          key === 'sku' || key === 'product_description' || key === 'description' ||
                                          (value && typeof value === 'object' && !Array.isArray(value) && !(value as any).uri)) return null;

                                        const displayValue = Array.isArray(value)
                                          ? `${value.length} item(s)`
                                          : (value !== null && value !== undefined ? String(value) : 'N/A');

                                        return (
                                          <XStack key={key} justifyContent="space-between" paddingVertical="$2" flexWrap="wrap">
                                            <SizableText size="$3" color={theme ? '#6B7280' : '#9CA3AF'} fontWeight="600" flex={1}>
                                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </SizableText>
                                            <SizableText size="$3" color={theme ? '#1F2937' : '#F9FAFB'} flex={2} numberOfLines={3} textAlign="right">
                                              {displayValue}
                                            </SizableText>
                                        </XStack>
                                        );
                                      })}
                                    </YStack>
                                  </YStack>
                                </Card>
                              )}

                              {/* Inventory Summary (if applicable) */}
                              {itemTypeConfig.showInventory && (
                                <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                                  <YStack gap="$3">
                                    <H4 color={theme ? '#1F2937' : '#F9FAFB'}>Inventory Summary</H4>
                                    <YStack gap="$2">
                                      <XStack justifyContent="space-between" alignItems="center" paddingVertical="$2">
                                        <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'} fontWeight="600">
                                          Current Stock
                                        </SizableText>
                                        <SizableText size="$4" color={theme ? '#1F2937' : '#F9FAFB'} fontWeight="700">
                                          {getField('stock') || getField('quantity') || 0} units
                                        </SizableText>
                                      </XStack>
                                      <Text style={{
                                        color: theme ? '#6B7280' : '#9CA3AF',
                                        fontSize: 12,
                                        textAlign: 'center',
                                        marginTop: 8,
                                        fontStyle: 'italic'
                                      }}>
                                        View detailed inventory management in the Inventory tab
                                      </Text>
                                    </YStack>
                                  </YStack>
                                </Card>
                              )}
                          </>
                        )}
                      </YStack>
                    )}

                    {activeTab === 'gallery' && (
                      <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                        <YStack gap="$4">
                          <XStack justifyContent="space-between" alignItems="center">
                            <H4 color={theme ? '#1F2937' : '#F9FAFB'}>Image Gallery</H4>
                            {getImages().length > 0 && (
                              <Pressable
                                onPress={() => {
                                  const images = getImages();
                                  if (images.length > 0) {
                                    setSelectedImageForEnhancement(images[0]);
                                    setSelectedImageIndex(0);
                                    setEnhancementModalVisible(true);
                                  }
                                }}
                                style={[
                                  styles.modernActionButton,
                                  getModernActionStyle(theme, 'blue'),
                                  {
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 8,
                                    paddingHorizontal: 16,
                                    paddingVertical: 10,
                                    borderRadius: 8
                                  }
                                ]}
                              >
                                <MaterialIcons name="auto-awesome" size={18} color={theme ? "black" : "white"} />
                                <Text style={[styles.modernFieldValue, { color: theme ? "black" : "white", fontWeight: '600' }]}>
                                  Enhance Image
                                </Text>
                              </Pressable>
                            )}
                          </XStack>
                          {getImages().length > 0 ? (
                            <YStack gap="$3">
                              <PhotoGallery images={getImages()} />
                              {/* Image Selection Grid for Enhancement */}
                              <YStack gap="$2">
                                <SizableText size="$3" color={getModernTextColor(theme, 'muted')}>
                                  Select an image to enhance:
                                </SizableText>
                                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                  <XStack gap="$2" paddingVertical="$2">
                                    {getImages().map((imageUrl, index) => (
                                      <Pressable
                                        key={index}
                                        onPress={() => {
                                          setSelectedImageForEnhancement(imageUrl);
                                          setSelectedImageIndex(index);
                                          setEnhancementModalVisible(true);
                                        }}
                                        style={{
                                          width: 80,
                                          height: 80,
                                          borderRadius: 8,
                                          overflow: 'hidden',
                                          borderWidth: 2,
                                          borderColor: selectedImageIndex === index ? 'rgb(90,192,234)' : 'transparent'
                                        }}
                                      >
                                        <Image
                                          source={{ uri: imageUrl }}
                                          style={{ width: '100%', height: '100%' }}
                                          contentFit="cover"
                                        />
                                        {selectedImageIndex === index && (
                                          <View style={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            backgroundColor: 'rgb(90,192,234)',
                                            borderRadius: 12,
                                            padding: 4
                                          }}>
                                            <MaterialIcons name="check" size={12} color="white" />
                                          </View>
                                        )}
                                      </Pressable>
                                    ))}
                                  </XStack>
                                </ScrollView>
                              </YStack>
                            </YStack>
                          ) : (
                            <YStack alignItems="center" gap="$3" padding="$4">
                              <MaterialIcons
                                name="photo-library"
                                size={48}
                                color={theme ? '#6B7280' : '#9CA3AF'}
                              />
                              <Text style={{ color: theme ? '#6B7280' : '#9CA3AF', textAlign: 'center' }}>
                                No images available. Add images using the camera or gallery buttons.
                              </Text>
                            </YStack>
                          )}
                        </YStack>
                      </Card>
                    )}

                    {activeTab === 'inventory' && (
                      <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                        <InventoryManagement dataId={contentId} />
                      </Card>
                    )}

                    {activeTab === 'suppliers' && (
                      <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                        <SupplierManagement productId={contentId} dataContent={dataContent} />
                      </Card>
                    )}

                    {activeTab === 'pricing' && (
                      <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                        <PricingManagement productId={contentId} />
                      </Card>
                    )}

                    {activeTab === 'quality' && (
                      <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                        <QualityControl productId={contentId} />
                      </Card>
                    )}

                    {activeTab === 'analytics' && (
                      <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" marginVertical="$2" borderRadius={16}>
                        <ProductAnalytics productId={contentId} />
                      </Card>
                    )}
                  </ScrollView>
              </YStack>

                {/* Quick Action Bar */}
                <Card backgroundColor={theme ? '#ffffff' : '#374151'} margin="$2" borderRadius={16}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{
                      paddingHorizontal: Platform.select({ default: 8, web: 12 }),
                      paddingVertical: Platform.select({ default: 8, web: 12 }),
                      gap: Platform.select({ default: 8, web: 12 })
                    }}
                  >
                    <XStack gap={Platform.select({ default: "$2", web: "$3" })} alignItems="center">
                      <QuickActionCard
                        icon={<MaterialIcons name="photo-camera" size={Platform.select({ default: 16, web: 20 })} color="rgb(90,192,234)" />}
                        label="Camera"
                        onPress={handleAddCamera}
                        theme={theme}
                      />

                      <QuickActionCard
                        icon={<MaterialIcons name="photo" size={Platform.select({ default: 16, web: 20 })} color="rgb(90,192,234)" />}
                        label="Gallery"
                        onPress={handleAddImage}
                        theme={theme}
                      />

                      <QuickActionCard
                        icon={<MaterialIcons name="edit" size={Platform.select({ default: 16, web: 20 })} color="rgb(90,192,234)" />}
                        label="Edit"
                        onPress={() => setEditOpen(true)}
                        theme={theme}
                      />

                      <QuickActionCard
                        icon={<MaterialIcons name="refresh" size={Platform.select({ default: 16, web: 20 })} color="rgb(90,192,234)" />}
                        label="Refresh"
                        onPress={() => refetch()}
                        theme={theme}
                      />
            </XStack>
                  </ScrollView>
                </Card>
        </YStack>
      )}

      {/* Image Enhancement Modal */}
      {selectedImageForEnhancement && (
        <ImageEnhancementModal
          visible={enhancementModalVisible}
          onClose={() => {
            setEnhancementModalVisible(false);
            setSelectedImageForEnhancement(null);
            setSelectedImageIndex(null);
          }}
          imageUrl={selectedImageForEnhancement}
          imageIndex={selectedImageIndex || undefined}
          onEnhancementComplete={async (enhancedImageUrl, imageIndex) => {
            try {
              // Upload the enhanced image to replace the original
              if (imageIndex !== undefined && imageIndex !== null) {
                // Convert enhanced image to base64 format for upload
                const imagePayload = {
                  uri: enhancedImageUrl,
                  fileName: `enhanced-image-${Date.now()}.png`,
                  mimeType: 'image/png'
                };

                // Add the enhanced image
                await apis.addDataImage(dataId, contentId, imagePayload);

                // Refetch to update the gallery
                refetch();

                Alert.alert('Success', 'Enhanced image has been added to the gallery');
              }
            } catch (error) {
              console.error('Error saving enhanced image:', error);
              Alert.alert('Error', 'Failed to save enhanced image. Please try again.');
            }
          }}
        />
      )}
    </DefaultScreen>
  );
};

export default ProductScreen;
